import Vue from 'vue'
import { Event } from 'vue-tables-2'
import moment from 'moment'
import { parsePhoneNumber } from 'libphonenumber-js'
import { mapGetters } from 'vuex'
import api from '@/lib/api'
import { capitalCase } from 'change-case'

const levelNames = {
  root: 'Root',
  org: 'Organization',
  site: 'Site',
  building: 'Building',
  floor: 'Floor',
  unit: 'Unit',
  area: 'Area'
}
const levelList = Object.keys(levelNames)

const batteryQueries = {
  healthy: JSON.stringify({ $gte: 2800 }),
  low: JSON.stringify({ $gte: 2600, $lt: 2800 }),
  depleted: JSON.stringify({ $gte: 0, $lt: 2600 }),
  unknown: JSON.stringify({ $not: { $gte: 0 } })
}

const batteryLevelDescriptions = {
  healthy: 'Healthy',
  low: 'Low',
  depleted: 'Depleted',
  unknown: 'Unknown'
}

const batteryLevelIcons = {
  healthy: 'fa fa-battery-three-quarters',
  low: 'fa fa-battery-quarter',
  depleted: 'fa fa-battery-empty',
  unstable: 'fa fa-exclamation-triangle',
  unknown: 'fa fa-exclamation-circle'
}

const batteryLevelEstimatedLifetimes = {
  low: '< 3 months',
  depleted: 'dead or dying',
  unknown: 'missing battery data'
}

const batteryLevelColors = {
  healthy: 'green',
  low: 'orange',
  depleted: 'red',
  unstable: 'red',
  unknown: 'gray'
}

const tagDistroStationTimeout = 3600 * 1000 // ms

const tagCollectionReasons = new Map([
  [
    'none',
    'Not flagged'
  ], [
    'any',
    'All needing collection'
  ], [
    'highUrgency',
    'Collect immediately'
  ], [
    'mediumUrgency',
    'Collect soon'
  ], [
    'noUrgency',
    'Collect regular'
  ], [
    'battery_depleted',
    'Battery depleted'
  ], [
    'tag_crash_high',
    'Tag Unstable (Urgent)'
  ], [
    'firmware_obsolete',
    'Obsolete Firmware'
  ], [
    'manual',
    'Manually flagged'
  ], [
    'battery_low',
    'Battery Low'
  ], [
    'tag_crash_low',
    'Tag Unstable'
  ], [
    'staff_turnover',
    'Staff Turnover'
  ], [
    'tag_replaced',
    'New tag assigned'
  ]
])

const tagCollectionUrgency = {
  0: 'Collect Immediately',
  1: 'Collect Soon',
  2: 'Collect When Possible'
}

const macRegEx = /([a-fA-F0-9]{12})/
const macLength = 12

const tagConfigStates = {
  flashed: 'Flashed',
  available: 'Available',
  reserved: 'Reserved',
  deployed: 'Deployed',
  ready_for_return: 'Ready for Return',
  returned: 'Returned',
  lost: 'Lost'
}

const gatewayConfigStates = {
  deployed: 'Deployed',
  ready_to_deploy: 'Ready to Deploy',
  undeployed: 'Undeployed',
  returned: 'Returned',
  lost: 'Lost',
  unknown: 'Unknown'
}

const roleNames = {
  noAccess: 'noAccess',
  default: 'securityReadOnly',
  security: 'security',
  warehouse: 'warehouse',
  factory: 'factory',
  distribution: 'distribution',
  distributionAdmin: 'distributionAdmin',
  it: 'it',
  customerAdmin: 'customerAdmin',
  customerSuperAdmin: 'customerSuperAdmin',
  admin: 'admin'
}

const alertCardColors = ['#4f8c9d', '#fa718e', '#68ed99', '#943d80', '#4cf32c', '#c712e8', '#378811', '#e689eb', '#bce333', '#4859ea', '#f4d403', '#f612a8', '#b8deb7', '#b42716', '#20d8fd', '#5f3e3f', '#edcbde', '#0a4f4e', '#f37e2f', '#1c4585']

export default {
  data () {
    return {
      levelNames,
      batteryQueries,
      batteryLevelDescriptions,
      batteryLevelIcons,
      batteryLevelEstimatedLifetimes,
      batteryLevelColors,
      gatewayConfigStates,
      tagConfigStates,
      tagDistroStationTimeout,
      tagCollectionReasons,
      tagCollectionUrgency,
      macRegEx,
      macLength,
      roleNames,
      alertCardColors
    }
  },
  computed: {
    ...mapGetters('auth', [
      'user',
      'refreshToken'
    ])
  },
  methods: {
    saveSetting (key, value) {
      window.sessionStorage.setItem(key, value)
    },
    loadSetting (key) {
      return window.sessionStorage.getItem(key)
    },
    clearSettings () {
      window.sessionStorage.clear()
    },
    savedFilter (field) {
      const key = `vuetables_${this.tableId}`
      const value = window.sessionStorage.getItem(key)
      if (value) {
        const state = JSON.parse(value)
        return (state.filterValues || {})[field] || (state.customQueries || {})[field] || (state.query || {})[field] || null
      }
      return null
    },
    changeFilter (field, value) {
      const key = `vuetables_${this.tableId}`
      const storage = JSON.parse(window.sessionStorage.getItem(key) || '{}')
      storage.filterValues = storage.filterValues || {}
      storage.filterValues[field] = value
      window.sessionStorage.setItem(key, JSON.stringify(storage))
      Event.$emit(`vue-tables.${this.tableId}.filter::${field}`, value && (value.id || value))
    },
    path (location) {
      if (Array.isArray(location)) {
        return location
          .slice(1)
          .map(loc => loc ? loc.name : '')
          .join(' > ')
      }
    },
    zoneId (location) {
      if (Array.isArray(location)) {
        const zone = location.slice(-1)[0]
        return (zone && zone.id) || zone
      }
    },
    zoneName (location) {
      if (Array.isArray(location)) {
        const zone = location.slice(-1)[0]
        return zone && zone.name
      }
    },
    locationById (location) {
      return location && location.map(loc => loc.id || loc)
    },
    sameLocation (loc1, loc2) {
      if (loc1 && loc2) {
        return loc1.map(l => l.id).join() === loc2.map(l => l.id).join()
      }
    },
    zoneIdAtLevel (location, level) {
      let zone
      if (!Array.isArray(location)) {
        return
      }
      if (location[0].level) {
        zone = location.find(l => l.level === level)
      }
      if (!zone) {
        zone = location[this.levelDepth(level)]
      }
      if (zone) {
        return zone.id || zone
      }
    },
    time (at, ago) {
      if (at) {
        const time = moment(at)
        return ago ? time.fromNow() : time.format('ddd, MMM D h:mm:ss A')
      }
      return ''
    },
    delay (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    editModal (options) {
      Vue.prototype.$editModal.open(options)
    },
    infoModal (options) {
      Vue.prototype.$infoModal.open(options)
    },
    urlPatternModal (options) {
      Vue.prototype.$urlPatternModal.open(options)
    },
    formatPhone (text) {
      try {
        const phoneNumber = parsePhoneNumber(text, 'US')
        return phoneNumber.formatNational()
      } catch (error) {
        return text
      }
    },
    formatContacts ({ sms, email, ascom, vocera, wctp, tiger } = {}) {
      const text = []
      try {
        if (sms) {
          const phoneNumber = parsePhoneNumber(sms, 'US')
          text.push(phoneNumber.formatNational())
        }
        if (email) {
          text.push(email)
        }
        if (ascom) {
          text.push(`[Ascom] ${ascom}`)
        }
        if (vocera) {
          text.push(`[Vocera] ${vocera}`)
        }
        if (wctp) {
          text.push(`[WCTP] ${wctp}`)
        }
        if (tiger) {
          text.push(`[TC] ${tiger}`)
        }
      } catch (error) {
        // ignore
      }
      return text.join(', ')
    },
    parsePhone (text) {
      try {
        const phoneNumber = parsePhoneNumber(text, 'US')
        return phoneNumber.number
      } catch (error) {
        throw new Error('Invalid phone number')
      }
    },
    levelName (id) {
      return levelNames[id]
    },
    levelDepth (level) {
      const userZone = this.user && this.user.location.slice(-1)[0]
      const userOffset = userZone ? levelList.indexOf(userZone.level) : 0
      return levelList.indexOf(level) - userOffset
    },
    childLevel (id) {
      const index = levelList.indexOf(id)
      if (index > -1) {
        return levelList[index + 1]
      }
    },
    zoneSort (a, b) {
      if (a.level === 'floor' && b.level === 'floor') {
        if (!a || !a.config || typeof a.config.number !== 'number') {
          return 1
        }
        if (!b || !b.config || typeof b.config.number !== 'number') {
          return -1
        }
        return a.config.number - b.config.number
      }
      if (a.name < b.name) {
        return -1
      }
      if (b.name < a.name) {
        return 1
      }
      return 0
    },
    randomName (prefix) {
      return `${prefix}-${Math.floor(Math.random(1000) * 1000)}`
    },
    tagOwnerOrLabel (tag) {
      const tagConfig = (tag || {}).config || {}
      // Note: a default 'tagConfig.label' is always returned by the API. The 'Someone' should never appear in practice.
      return (tagConfig.owner || {}).name || tagConfig.label || 'Someone'
    },
    loginToUrlWithRole (location, slug, role, launchNewWindow) {
      const { protocol, hostname, host, pathname } = location
      let query = `?token=${this.refreshToken.token}`
      if (role) {
        query += `&role=${role}`
      }
      if (!slug) {
        return alert('Unable to login to this zone')
      }
      let newHref = null
      if (hostname === 'localhost') {
        newHref = `${location.href}${query}&slug=${slug}`
      } else {
        const parts = host.split('.')
        const newHost = [slug, ...parts.slice(1)].join('.')
        newHref = `${protocol}//${newHost}${pathname}${query}`
      }
      if (launchNewWindow) {
        window.open(newHref)
      } else {
        window.location.href = newHref
      }
    },
    hashCode (str) {
      return Math.abs(Array.from(str).reduce((s, c) => Math.imul(31, s) + c.charCodeAt(0) | 0, 0))
    },
    async googleDownload (path) {
      const { data: url } = await api.get(`storage/link/${path}`)
      if (url) {
        window.open(url)
      }
    },
    async getTagLogs (tag, flagsOnly, startTime) {
      const query = {
        operation: 'update',
        ...(flagsOnly && { 'changes.config.collection': { $exists: true } }),
        ...(startTime && { at: { $gte: startTime } })
      }
      const { data: events } = await api.get(`log/audit?id=${tag}&query=${JSON.stringify(query)}&orderBy=at`)
      return events.data || []
    },
    parseRoutineId (id) {
      let [group, ...title] = id.split('_')
      if (!title.length) {
        title = [group]
        group = 'other'
      }
      title = title.join('_')
      return {
        group: capitalCase(group),
        title: capitalCase(title)
      }
    }
  }
}
